import React, { useState, useRef, useEffect, JSX } from 'react';
import { TooltipProps } from '../../tooltip/tooltip';
import Tooltip from '../../tooltip/tooltip';
import Icon, { IconType } from '../../icon/Icon';
import classNames from 'classnames';

import './SelectField.scss';
import { FieldState } from '../Field';
import SortingSlideout from '../../slideout/sorting-mobile/sortingMobileSlideout';

interface Option {
  label: string;
  labelShort: string;
  name: string;
  sortLabelAscending?: string;
  sortLabelDescending?: string;
  value?: string;
}

interface Alignment {
  horizontal?: 'left' | 'center' | 'right';
  vertical?: 'top' | 'center' | 'bottom';
}

interface SelectProps {
  id: string;
  options: Option[];
  value?: string;
  onChange: (value: string) => void;
  hideLabel?: boolean;
  tooltip?: TooltipProps;
  description?: string;
  errorMessage?: string;
  state?: FieldState;
  required?: boolean;
  placeholder?: string;
  label?: string;
  className?: string;
  style?: React.CSSProperties;
  dropdownStyle?: React.CSSProperties;
  alignment?: Alignment;
  isMobile?: boolean;
}

const Select: React.FC<SelectProps> = ({
  id,
  options,
  value,
  onChange,
  hideLabel = false,
  tooltip,
  description,
  errorMessage,
  required = false,
  state = 'default',
  placeholder = 'Select...',
  className = '',
  style = {},
  dropdownStyle = {},
  label,
  alignment = { horizontal: 'left', vertical: 'bottom' },
  isMobile = false,
}) => {
  const isControlled = value !== undefined;

  // Set initial value: if not controlled, use the first option by default
  const [internalValue, setInternalValue] = useState<string>(
    value ?? options[0]?.value ?? ''
  );

  const selectedValue = isControlled ? value : internalValue;
  const selected = options.find((opt) => {
    const asc = `${opt.name}-asc`;
    const desc = `${opt.name}-desc`;

    return (
      opt.value === selectedValue ||
      asc === selectedValue ||
      desc === selectedValue
    );
  });

  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const hasError = state === 'error';
  const isDisabled = state === 'disabled';

  useEffect(() => {
    if (isMobile) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile]);

  const handleSelect = (selectedVal: string) => {
    console.log(`Selected value: ${selectedVal}`);
    if (!isControlled) {
      setInternalValue(selectedVal);
    }

    onChange(selectedVal);
    setIsOpen(false);
  };

  return (
    <div
      className={classNames('select-field', className)}
      ref={ref}
      style={style}
    >
      {label && (
        <label
          htmlFor={id}
          className={classNames('select-label', {
            'is-sr-only': hideLabel,
            'has-error': hasError,
          })}
        >
          <span className="select-label-content">
            {required && <span className="u-color-error">*</span>}
            {label}
            {tooltip && (
              <Tooltip {...tooltip}>
                <Icon
                  iconType={IconType.Info}
                  className="select-tooltip-icon"
                />
              </Tooltip>
            )}
          </span>
        </label>
      )}

      <div
        className={classNames('select-container', {
          'is-disabled': isDisabled,
        })}
        onClick={(e) => {
          if (isMobile && isOpen) {
            e.stopPropagation();
            return;
          }
          if (!isDisabled) {
            setIsOpen((prev) => !prev);
          }
        }}
      >
        <div
          id={id}
          tabIndex={isDisabled ? -1 : 0}
          className={classNames(
            'select-box',
            {
              'has-error': hasError,
              'is-disabled': isDisabled,
              'is-open': isOpen,
            },
            'filter-sort-buttons'
          )}
        >
          <span className="select-label-header">
            {selected?.label || placeholder}
          </span>
          <Icon
            iconType={isOpen ? IconType.ArrowUp : IconType.ArrowDown}
            className="select-icon u-icon-small"
          />
        </div>

        {isOpen && !isDisabled && !isMobile && (
          <ul
            className={classNames(
              'select-dropdown',
              `align-horizontal-${alignment.horizontal || 'left'}`,
              `align-vertical-${alignment.vertical || 'bottom'}`
            )}
            style={dropdownStyle}
          >
            {options.flatMap((option) => {
              const baseLabel =
                option.labelShort || option.label || option.name;
              const result: JSX.Element[] = [];

              if (option.sortLabelAscending) {
                const valueAsc = `${option.name}-asc`;
                result.push(
                  <li
                    key={valueAsc}
                    className={classNames('select-option', {
                      'is-selected': selectedValue === valueAsc,
                    })}
                    onMouseDown={() => handleSelect(valueAsc)}
                  >
                    <span>
                      {baseLabel} ({option.sortLabelAscending})
                    </span>
                  </li>
                );
              }

              if (option.sortLabelDescending) {
                const valueDesc = `${option.name}-desc`;
                result.push(
                  <li
                    key={valueDesc}
                    className={classNames('select-option', {
                      'is-selected': selectedValue === valueDesc,
                    })}
                    onMouseDown={() => handleSelect(valueDesc)}
                  >
                    <span>
                      {baseLabel} ({option.sortLabelDescending})
                    </span>
                  </li>
                );
              }

              if (!option.sortLabelAscending && !option.sortLabelDescending) {
                result.push(
                  <li
                    key={option.name}
                    className={classNames('select-option', {
                      'is-selected': selectedValue === option.name,
                    })}
                    onMouseDown={() => handleSelect(option.name)}
                  >
                    <span>{baseLabel}</span>
                  </li>
                );
              }

              return result;
            })}
          </ul>
        )}

        {isOpen && isMobile && !isDisabled && (
          <SortingSlideout
            isVisible={isOpen}
            onClose={() => setIsOpen(false)}
            sortingOptions={options.flatMap((option) => {
              const baseLabel =
                option.labelShort || option.label || option.name;
              const opts: { value: string; label: string }[] = [];

              if (option.sortLabelAscending) {
                opts.push({
                  value: `${option.name}-asc`,
                  label: `${baseLabel} (${option.sortLabelAscending})`,
                });
              }
              if (option.sortLabelDescending) {
                opts.push({
                  value: `${option.name}-desc`,
                  label: `${baseLabel} (${option.sortLabelDescending})`,
                });
              }
              if (!option.sortLabelAscending && !option.sortLabelDescending) {
                opts.push({
                  value: option.name,
                  label: baseLabel,
                });
              }

              return opts;
            })}
            activeOption={selectedValue}
            onApply={(selected) => {
              handleSelect(selected);
              onChange(selected);
              setIsOpen(false);
            }}
            onClear={() => {
              setIsOpen(false);
            }}
          />
        )}
      </div>

      {hasError && errorMessage && (
        <p className="select-error-message" role="alert" aria-live="assertive">
          {errorMessage}
        </p>
      )}

      {!hasError && description && (
        <p className="select-description">{description}</p>
      )}
    </div>
  );
};

export default Select;
